<template>
  <div class="min-h-screen bg-gradient-to-br from-psychology-calm via-white to-psychology-warm">
    <!-- 顶部导航 -->
    <nav class="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <router-link to="/" class="inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
                <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              </svg>
              首页
            </router-link>
            <div class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-psychology-primary">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
              <span class="font-semibold text-psychology-primary">{{ assessmentType === 'self' ? '给自己测' : '为恋人测' }}</span>
            </div>
          </div>
          <button @click="goBack" class="hidden sm:inline-flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-md transition-colors text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
            返回
          </button>
          <button @click="goBack" class="sm:hidden inline-flex items-center justify-center p-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-md transition-colors text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
          </button>
        </div>

        <!-- 开发模式快速选择面板 -->
        <div v-if="isDevelopment" class="mt-4 mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div class="flex items-center gap-4">
            <span class="text-sm font-semibold text-yellow-800">开发模式快速选择：</span>
            <div class="flex gap-2">
              <button 
                v-for="value in [1, 2, 3, 4, 5]" 
                :key="value"
                @click="quickSelectAll(value)"
                class="px-3 py-1 text-sm bg-white border border-yellow-300 rounded hover:bg-yellow-100 transition-colors"
              >
                全部选 {{ value }}
              </button>
            </div>
            <button 
              @click="randomSelectAll"
              class="px-3 py-1 text-sm bg-purple-100 text-purple-700 border border-purple-300 rounded hover:bg-purple-200 transition-colors"
            >
              随机选择
            </button>
            <button 
              @click="clearAllAnswers"
              class="px-3 py-1 text-sm bg-red-100 text-red-700 border border-red-300 rounded hover:bg-red-200 transition-colors"
            >
              清空所有
            </button>
            <button 
              @click="handleSubmit"
              :disabled="!canSubmit"
              :class="[
                'px-3 py-1 text-sm border rounded transition-colors',
                canSubmit 
                  ? 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200' 
                  : 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
              ]"
            >
              快速提交
            </button>
            <span class="text-xs text-yellow-600">
              已选择: {{ answeredCount }}/{{ totalQuestions }}
            </span>
          </div>
        </div>

        <!-- 整体进度条 -->
        <div class="mt-4">
          <div class="flex justify-between items-center mb-2">
            <span class="text-sm text-gray-600">整体进度</span>
            <span class="text-sm font-medium">75%</span>
          </div>
          <div class="relative w-full overflow-hidden rounded-full bg-gray-200 h-2">
            <div class="h-full bg-psychology-primary transition-all" style="width: 75%;"></div>
          </div>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <main class="container mx-auto px-4 py-8">
      <div class="max-w-6xl mx-auto space-y-6">
        <!-- 问卷进度卡片 -->
        <QuestionnaireProgress 
          :total="totalQuestions"
          :answered="answeredCount"
          :unanswered="unansweredCount"
          :required-unanswered="requiredUnansweredCount"
          :assessment-type="assessmentType"
        />

        <!-- 题目列表（按维度分组） -->
        <DimensionQuestions 
          v-for="(dimension, index) in dimensions"
          :key="dimension.id"
          :dimension="dimension"
          :dimension-index="index"
          :answers="answers"
          @answer="handleAnswer"
        />

        <!-- 底部导航按钮 -->
        <div class="flex justify-between items-center pt-6 border-t">
          <button 
            @click="goBack"
            class="btn-secondary"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
            返回上一步
          </button>
          <button 
            @click="handleSubmit"
            :disabled="!canSubmit"
            :class="[
              'btn-primary ml-auto',
              !canSubmit && 'opacity-50 cursor-not-allowed'
            ]"
          >
            提交评估
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
              <path d="M5 12h14"></path>
              <path d="m12 5 7 7-7 7"></path>
            </svg>
          </button>
        </div>
      </div>
    </main>

    <!-- 回到顶部 -->
    <BackToTop />
  </div>
</template>

<script>
import { questionnaireData, getAllQuestions } from '../data/questions.js'
import QuestionnaireProgress from '../components/assessment/QuestionnaireProgress.vue'
import DimensionQuestions from '../components/assessment/DimensionQuestions.vue'
import BackToTop from '../components/BackToTop.vue'
import { useToast } from 'vue-toastification'

export default {
  name: 'QuestionnairePage',
  components: {
    QuestionnaireProgress,
    DimensionQuestions,
    BackToTop
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      assessmentType: 'self',
      answers: {}, // { questionId: answerValue }
      dimensions: [],
      allQuestions: [], // 所有题目的扁平化列表
      saveTimer: null, // 防抖定时器
      lastSaveTime: null, // 最后保存时间
      isDevelopment: process.env.NODE_ENV === 'development' // 开发模式检测
    }
  },
  computed: {
    totalQuestions() {
      return this.allQuestions.length
    },
    answeredCount() {
      return Object.keys(this.answers).length
    },
    unansweredCount() {
      return this.totalQuestions - this.answeredCount
    },
    requiredUnansweredCount() {
      // 所有题目都是必答题
      return this.unansweredCount
    },
    canSubmit() {
      // 确保所有题目都已回答，且答案都是有效的
      if (this.totalQuestions === 0) return false
      
      // 检查答案数量是否匹配
      if (this.answeredCount !== this.totalQuestions) return false
      
      // 验证所有答案的题目ID都在当前测评类型的题目列表中
      const currentQuestionIds = new Set(this.allQuestions.map(q => q.id))
      const answerQuestionIds = Object.keys(this.answers)
      
      // 确保所有答案的题目ID都是有效的
      const allAnswersValid = answerQuestionIds.every(questionId => 
        currentQuestionIds.has(questionId)
      )
      
      return allAnswersValid
    }
  },
  created() {
    this.assessmentType = this.$route.query.type || 'self'
    this.dimensions = questionnaireData[this.assessmentType].dimensions
    this.allQuestions = getAllQuestions(this.assessmentType)
    
    // 清理可能存在的无效数据
    this.cleanupInvalidData()
    
    // 尝试从localStorage恢复答案
    this.loadAnswers()
  },
  beforeUnmount() {
    // 组件销毁前清理定时器并保存
    if (this.saveTimer) {
      clearTimeout(this.saveTimer)
      this.saveAnswersImmediately()
    }
  },
  methods: {
    goBack() {
      // 检查是否评测已完成，如果是则返回首页
      const assessmentCompleted = localStorage.getItem('rpi_assessment_completed')
      if (assessmentCompleted === 'true') {
        this.$router.push('/')
      } else {
        this.$router.go(-1)
      }
    },
    handleAnswer({ questionId, value }) {
      this.answers = {
        ...this.answers,
        [questionId]: value
      }
      // 使用防抖机制延迟保存（1秒后）
      this.debounceSave()
    },
    debounceSave() {
      // 清除之前的定时器
      if (this.saveTimer) {
        clearTimeout(this.saveTimer)
      }
      
      // 设置新的定时器，1秒后保存
      this.saveTimer = setTimeout(() => {
        this.saveAnswersImmediately()
      }, 1000)
    },
    saveAnswersImmediately() {
      try {
        // 按测评类型分别存储答案
        const storageKey = `rpi_answers_${this.assessmentType}`
        localStorage.setItem(storageKey, JSON.stringify(this.answers))
        // 保存进度信息
        const progress = {
          answers: this.answers,
          assessmentType: this.assessmentType,
          timestamp: new Date().toISOString(),
          progress: Math.round((this.answeredCount / this.totalQuestions) * 100)
        }
        localStorage.setItem('rpi_assessment_progress', JSON.stringify(progress))
        this.lastSaveTime = new Date()
      } catch (e) {
        console.error('保存进度失败:', e)
        // 只在保存失败时显示错误提示
        this.toast.error('保存失败，请检查浏览器存储空间')
      }
    },
    loadAnswers() {
      // 按测评类型加载对应的答案
      const storageKey = `rpi_answers_${this.assessmentType}`
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        try {
          const savedAnswers = JSON.parse(saved)
          // 验证并过滤答案，只保留属于当前测评类型的题目答案
          this.answers = this.validateAndFilterAnswers(savedAnswers)
          const answerCount = Object.keys(this.answers).length
          // 只有当有较多答案时才显示恢复提示（至少3题）
          if (answerCount >= 3) {
            this.toast.info(`已恢复上次的答题进度（${answerCount}/${this.totalQuestions}题）`, {
              timeout: 3000
            })
          }
        } catch (e) {
          console.error('Failed to load answers:', e)
          this.answers = {}
        }
      } else {
        this.answers = {}
      }
    },
    validateAndFilterAnswers(savedAnswers) {
      // 获取当前测评类型的所有题目ID
      const currentQuestionIds = new Set(this.allQuestions.map(q => q.id))
      
      // 过滤出属于当前测评类型的答案
      const validAnswers = {}
      Object.keys(savedAnswers).forEach(questionId => {
        if (currentQuestionIds.has(questionId)) {
          validAnswers[questionId] = savedAnswers[questionId]
        }
      })
      
      return validAnswers
    },
    // 开发模式快速选择功能
    quickSelectAll(value) {
      const newAnswers = {}
      this.allQuestions.forEach(question => {
        newAnswers[question.id] = value
      })
      this.answers = newAnswers
      this.saveAnswersImmediately()
      this.toast.success(`已为所有 ${this.totalQuestions} 道题选择答案 ${value}`, {
        timeout: 2000
      })
    },
    randomSelectAll() {
      const newAnswers = {}
      this.allQuestions.forEach(question => {
        // 随机选择 1-5 之间的值
        newAnswers[question.id] = Math.floor(Math.random() * 5) + 1
      })
      this.answers = newAnswers
      this.saveAnswersImmediately()
      this.toast.success(`已为所有 ${this.totalQuestions} 道题随机选择答案`, {
        timeout: 2000
      })
    },
    clearAllAnswers() {
      this.answers = {}
      this.saveAnswersImmediately()
      this.toast.info('已清空所有答案', {
        timeout: 2000
      })
    },
    cleanupInvalidData() {
      // 清理旧的统一存储键（如果存在）
      const oldAnswers = localStorage.getItem('rpi_answers')
      if (oldAnswers) {
        try {
          const parsedAnswers = JSON.parse(oldAnswers)
          // 如果旧数据包含当前测评类型的答案，迁移到新的存储键
          const currentQuestionIds = new Set(this.allQuestions.map(q => q.id))
          const validOldAnswers = {}
          Object.keys(parsedAnswers).forEach(questionId => {
            if (currentQuestionIds.has(questionId)) {
              validOldAnswers[questionId] = parsedAnswers[questionId]
            }
          })
          
          if (Object.keys(validOldAnswers).length > 0) {
            const storageKey = `rpi_answers_${this.assessmentType}`
            localStorage.setItem(storageKey, JSON.stringify(validOldAnswers))
          }
          
          // 删除旧的存储键
          localStorage.removeItem('rpi_answers')
        } catch (e) {
          console.error('清理旧数据时出错:', e)
          // 如果解析失败，直接删除旧数据
          localStorage.removeItem('rpi_answers')
        }
      }
      
      // 清理可能过期的进度数据
      const progress = localStorage.getItem('rpi_assessment_progress')
      if (progress) {
        try {
          const parsedProgress = JSON.parse(progress)
          // 如果进度数据的测评类型与当前不匹配，清除它
          if (parsedProgress.assessmentType !== this.assessmentType) {
            localStorage.removeItem('rpi_assessment_progress')
          }
        } catch (e) {
          console.error('清理进度数据时出错:', e)
          localStorage.removeItem('rpi_assessment_progress')
        }
      }
    },
    handleSubmit() {
      if (!this.canSubmit) {
        const remaining = this.requiredUnansweredCount
        this.toast.warning(`还有 ${remaining} 道必答题未完成，请继续填写`, {
          timeout: 4000
        })
        return
      }
      
      // 最终验证答案完整性
      const finalAnswers = this.validateAndFilterAnswers(this.answers)
      if (Object.keys(finalAnswers).length !== this.totalQuestions) {
        this.toast.error('答案验证失败，请重新答题', {
          timeout: 3000
        })
        return
      }
      
      // 保存答案（使用类型特定的存储键）
      const storageKey = `rpi_answers_${this.assessmentType}`
      localStorage.setItem(storageKey, JSON.stringify(finalAnswers))
      localStorage.setItem('rpi_submission_time', new Date().toISOString())
      
      // 清除进度
      localStorage.removeItem('rpi_assessment_progress')
      
      // 显示成功提示
      this.toast.success('测评完成！正在生成结果...', {
        timeout: 2000
      })
      
      // 跳转到计算中间页
      setTimeout(() => {
        this.$router.push({
          path: '/assessment/calculating',
          query: { type: this.assessmentType }
        })
      }, 500)
    }
  }
}
</script>

